import React, { useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

type TiptapEditorProps = {
    value?: string;
    onChange: (html: string) => void;
    placeholder?: string;
};

function ToolbarButton({
    onClick,
    active = false,
    disabled = false,
    children,
}: {
    onClick: () => void;
    active?: boolean;
    disabled?: boolean;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={[
                "rounded-lg border px-3 py-1.5 text-sm font-medium transition",
                active
                    ? "border-indigo-600 bg-indigo-600 text-white"
                    : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50",
                disabled ? "cursor-not-allowed opacity-50" : "",
            ].join(" ")}
        >
            {children}
        </button>
    );
}

export default function TiptapEditor({
    value = "",
    onChange,
    placeholder,
}: TiptapEditorProps) {
    const editor = useEditor({
        extensions: [StarterKit],
        content: value || "<p></p>",
        immediatelyRender: false,
        editorProps: {
            attributes: {
                class:
                    "prose max-w-none min-h-[240px] focus:outline-none px-4 py-3",
            },
        },
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
    });

    useEffect(() => {
        if (!editor) return;

        const currentHtml = editor.getHTML();
        if (value !== currentHtml) {
            editor.commands.setContent(value || "<p></p>", { emitUpdate: false });
        }
    }, [value, editor]);

    if (!editor) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-500">
                Loading editor...
            </div>
        );
    }

    return (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-wrap gap-2 border-b border-gray-200 bg-gray-50 p-3">
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    active={editor.isActive("bold")}
                >
                    Bold
                </ToolbarButton>

                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    active={editor.isActive("italic")}
                >
                    Italic
                </ToolbarButton>

                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    active={editor.isActive("strike")}
                >
                    Strike
                </ToolbarButton>

                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    active={editor.isActive("heading", { level: 2 })}
                >
                    H2
                </ToolbarButton>

                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    active={editor.isActive("bulletList")}
                >
                    Bullet List
                </ToolbarButton>

                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    active={editor.isActive("orderedList")}
                >
                    Ordered List
                </ToolbarButton>

                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    active={editor.isActive("blockquote")}
                >
                    Quote
                </ToolbarButton>

                <ToolbarButton
                    onClick={() => editor.chain().focus().setHorizontalRule().run()}
                >
                    Divider
                </ToolbarButton>

                <ToolbarButton
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().chain().focus().undo().run()}
                >
                    Undo
                </ToolbarButton>

                <ToolbarButton
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().chain().focus().redo().run()}
                >
                    Redo
                </ToolbarButton>
            </div>

            {placeholder && !editor.getText().length ? (
                <div className="pointer-events-none absolute px-4 py-3 text-sm text-gray-400">
                    {placeholder}
                </div>
            ) : null}

            <EditorContent editor={editor} />
        </div>
    );
}